import api from '@Api/Api'
import Api, { rtkPrepareHeadersCallback } from '@Api/Api'
import { RootState } from '@Redux/store'
import LocalStorageKeys from '@Constants/LocalStorageKeys'
import { setAuthData } from '@Features/Authentication/AuthenticationSlice/Authentication.slice'
import { BaseApi } from '@Features/Base/BaseApi/BaseApi'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import DashboardAPI from '../DashboardApi/DashboardApi'
import {
	DashboardInitalState,
	EditProfilePayload,
	Invoice,
	MethodRates,
	ProviderWithPayload,
	RechargeCallbackPayload,
	RechargePayloadType,
	RechargeProvider,
	RechargeRequestCallback,
	RechargeRequestPayload,
	RequestCallbackPayload,
	TimelineEvent,
	WalletHistory,
} from './Dashboard.slice.types'

const initialState: DashboardInitalState = {
	walletHistory: null,
	timelineEvents: null,
	userClassrooms: [],
	loaders: {
		userClassrooms: 'idle',
		walletHistory: 'idle',
		timelineEvents: 'idle',
	},
}

const SLICE_BASE_NAME = 'dashboard'

export const getWalletHistory = createAsyncThunk(`${SLICE_BASE_NAME}/wallethistory`, async (filters?: FiltersPayload) => {
	const { data } = await DashboardAPI.getInstance().getWalletHistory(filters)
	return data
})

export const getTimelineEvents = createAsyncThunk(`${SLICE_BASE_NAME}/timeline-events`, async (filters?: FiltersPayload) => {
	const { data } = await DashboardAPI.getInstance().getTimelineEvents(filters)
	return data
})

export const getUserClassrooms = createAsyncThunk(`${SLICE_BASE_NAME}/user-classrooms`, async () => {
	const { data } = await DashboardAPI.getInstance().getUserClassrooms()
	return data
})

export const getUserInvoices = createAsyncThunk(`${SLICE_BASE_NAME}/user-invoices`, async () => {
	const { data } = await DashboardAPI.getInstance().getUserInvoices()
	return data
})

export const dashboardAPI = BaseApi.enhanceEndpoints({
	addTagTypes: ['Profile', 'Invoices', 'Instructors', 'Classrooms', 'Redirection'],
}).injectEndpoints({
	endpoints: builder => ({
		getWalletHistory: builder.query<ApiResponse<WalletHistory[]>, FiltersPayload | null>({
			query: (filters: FiltersPayload | null) => {
				return { url: 'student/profile/wallet/history', body: filters ?? {}, method: 'POST' }
			},
		}),
		getTimelineEvents: builder.query<ApiResponse<TimelineEvent[]>, FiltersPayload | null>({
			query: (filters: FiltersPayload) => ({ url: 'student/profile/timeline', body: filters ?? {}, method: 'POST' }),
		}),
		getUserInvoices: builder.query<ApiResponse<Invoice[]>, FiltersPayload | null>({
			query: (filters: FiltersPayload | null) => ({ url: 'student/profile/invoices', method: 'POST', body: filters ?? {} }),
			providesTags: ['Invoices'],
		}),
		getUserClassrooms: builder.query<ApiResponse<Classroom[]>, void>({
			query: () => ({ url: 'student/profile/classrooms' }),
		}),
		/**
		 * Body shape, not a bare id.
		 *
		 * The endpoint validates `classroom_id: schema.number.optional()` off the
		 * request BODY, and this query passes its argument straight through as the
		 * body — so `{ classroom_id: N }` scopes it to one classroom and no
		 * argument returns platform-wide counts. Both call sites already do
		 * exactly that; the type said `number | void`, which described neither.
		 *
		 * Worth being precise about: the type was the dangerous half. "Fixing" the
		 * call to match it would have sent a bare number as the body, which
		 * validates to nothing and silently returns platform-wide numbers on a
		 * per-classroom widget.
		 */
		getClassroomsAnalytics: builder.query<ApiResponse<ClassroomsAnalytics>, { classroom_id: number } | void>({
			query: payload => ({
				url: 'student/analytics/count',
				method: 'POST',
				body: payload,
			}),
		}),
		getUserProfile: builder.query<ApiResponse<StudentUser> | null, void>({
			providesTags: ['Profile'],
			async queryFn(args, { getState }) {
				// Was gated on a localStorage blob existing. The credential is an
				// httpOnly cookie now, so the only honest source of "am I signed
				// in" is the state bootstrapAuthAction filled from /auth/me.
				const { isAuth, deviceUnverified } = (getState() as RootState).auth
				// A pending device 401s on everything but the challenge endpoints, so
				// firing this on the verification screen only produces a steady stream
				// of failed requests and error-log noise.
				if (isAuth && !deviceUnverified) {
					const { data } = await api.get<ApiResponse<StudentUser>>('student/profile')
					return { data }
				}
				// An ERROR, not `{ data: null }`. A null cached as a fulfilled success
				// never refetches on its own — any unskipped subscriber whose queryFn
				// ran while auth was unresolved poisoned the shared entry for the
				// life of the page (the invalidateTags calls in App and VerifyDevice
				// exist to undo exactly that). A rejected entry that never fulfilled
				// re-runs on the next subscription, so the cache self-heals the
				// moment a signed-in consumer asks again — and if a real profile was
				// already cached, a later rejection keeps serving it instead of
				// blanking the UI during a device-verification window.
				return { error: { status: 'CUSTOM_ERROR', error: 'profile unavailable while signed out or device-unverified' } }
			},
		}),
		changePassword: builder.query<ApiResponse<any>, ChangePasswordPayload>({
			query: payload => ({ url: 'student/profile/password/change', method: 'PUT', body: payload }),
		}),
		editUserProfile: builder.mutation<ApiResponse<any>, { data: EditProfilePayload; type?: 'educational_info' }>({
			query: payload => ({ url: 'student/profile', method: 'PUT', body: payload.data }),
			invalidatesTags: result => {
				return ['Classrooms', 'Instructors', 'Profile']
			},
		}),
		getMethodRates: builder.query<ApiResponse<MethodRates>, RechargeProvider | undefined>({
			query: provider => ({
				url: `recharge/rates?method=${provider}`,
			}),
		}),
		uploadProfilePicture: builder.mutation<ApiResponse<void>, FormData>({
			query: formData => ({ url: 'student/profile/picture/upload', method: 'POST', body: formData }),
			invalidatesTags: ['Profile'],
		}),
		proccedRechargeRequest: builder.mutation<RechargePayloadType, RechargeRequestPayload>({
			query: (payload: RechargeRequestPayload) => {
				let url = `recharge`
				return {
					url,
					method: 'POST',
					body: payload,
				}
			},

			transformResponse: (response: ApiResponse<any>, meta, arg) => {
				const { provider, method } = arg
				const _: RechargeRequestCallback<typeof provider, typeof method> = {
					method: method,
					provider: provider,
					payload: response.data,
				}
				return _ as RechargePayloadType
			},
		}),
		proccedScratchCodeRequest: builder.mutation<any, string>({
			query: code => ({
				url: 'recharge/direct',
				body: {
					code: code,
				},
				method: 'POST',
			}),
			invalidatesTags: result => (result ? ['Profile'] : []),
		}),
		sendRechargeCallback: builder.query<any, RechargeCallbackPayload>({
			query: payload => ({
				url: 'recharge/callback',
				body: payload,
				method: 'POST',
			}),
		}),
		sendRollback: builder.query<any, string>({
			query: id => ({
				url: 'recharge/rollback',
				body: {
					reference_number: id,
				},
				method: 'POST',
			}),
		}),
		checkRedirection: builder.query<ApiResponse<AuthUserData>, { classroom_id?: number }>({
			query: payload => ({ url: `student/profile/redirect/check/${payload.classroom_id ?? ''}` }),
			providesTags: ['Redirection'],
			async onQueryStarted(arg, api) {
				try {
					const {
						data: {
							data: { redirect },
						},
					} = await api.queryFulfilled
					await api.dispatch(setAuthData({ redirect }))
				} catch (error) {}
			},
		}),
	}),
})
export const {
	useGetWalletHistoryQuery,
	useLazyGetWalletHistoryQuery,
	useGetTimelineEventsQuery,
	useLazyGetTimelineEventsQuery,
	useGetUserInvoicesQuery,
	useGetUserClassroomsQuery,
	useGetUserProfileQuery,
	useLazyGetUserProfileQuery,
	useProccedScratchCodeRequestMutation,
	useProccedRechargeRequestMutation,
	useGetMethodRatesQuery,
	useUploadProfilePictureMutation,
	useEditUserProfileMutation,
	useLazySendRechargeCallbackQuery,
	useLazyCheckRedirectionQuery,
	useLazyChangePasswordQuery,
	useGetClassroomsAnalyticsQuery,
} = dashboardAPI
